import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiClientError } from '../lib/api'
import { clearSession, loadSession } from '../lib/storage'
import type { MeResponse } from '@onlybans/shared'

export function Dashboard() {
  const navigate = useNavigate()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const session = loadSession()
    if (!session) {
      navigate('/login', { replace: true })
      return
    }
    void (async () => {
      try {
        const resp = await api.me(session.token)
        setMe(resp)
        // Re-emit LoginSuccess so the OnlyBans extension (if installed
        // *after* initial sign-in, or just freshly installed) can pick
        // up the existing session token without requiring a re-login.
        document.dispatchEvent(
          new CustomEvent('OnlyBansLoginSuccess', {
            detail: {
              sessionToken: session.token,
              expiresAt: session.expiresAt,
              user: resp.user,
              subscription: resp.subscription,
            },
          }),
        )
      } catch (e) {
        if (e instanceof ApiClientError && e.status === 401) {
          clearSession()
          navigate('/login', { replace: true })
          return
        }
        setError(e instanceof Error ? e.message : 'Failed to load')
      } finally {
        setLoading(false)
      }
    })()
  }, [navigate])

  const onLogout = async () => {
    const session = loadSession()
    if (session) {
      try {
        await api.logout(session.token)
      } catch {
        /* swallow — we'll clear locally anyway */
      }
    }
    clearSession()
    document.dispatchEvent(new CustomEvent('OnlyBansLogout'))
    navigate('/login', { replace: true })
  }

  if (loading) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-zinc-400">加载中…</div>
  }
  if (error) {
    return <div className="mx-auto max-w-3xl px-6 py-16 text-red-400">{error}</div>
  }
  if (!me) return null

  const sub = me.subscription
  const isActive =
    sub.status === 'active' &&
    (sub.currentPeriodEnd == null || sub.currentPeriodEnd > Date.now())

  return (
    <div className="mx-auto max-w-3xl px-6 py-12 space-y-8">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">仪表盘</h1>
        <div className="mt-4 rounded-md border border-zinc-800 p-4 flex items-center gap-4">
          {me.user.picture ? (
            <img
              src={me.user.picture}
              alt=""
              className="h-12 w-12 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : null}
          <div>
            <div className="font-medium">{me.user.name ?? me.user.email}</div>
            <div className="text-sm text-zinc-400">{me.user.email}</div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">订阅状态</h2>
        {isActive ? (
          <div className="mt-3 rounded-md border border-emerald-700/50 bg-emerald-900/20 p-4">
            <p className="text-sm">
              <span className="font-medium text-emerald-400">已开通</span>
              {sub.currentPeriodEnd ? (
                <>
                  ，有效期至{' '}
                  {new Date(sub.currentPeriodEnd).toLocaleDateString('zh-CN')}
                </>
              ) : null}
            </p>
            <p className="mt-2 text-sm text-zinc-400">
              你已经可以在浏览器扩展里使用「一键 Block」功能。
            </p>
          </div>
        ) : (
          <div className="mt-3 rounded-md border border-amber-700/50 bg-amber-900/20 p-4">
            <p className="text-sm font-medium text-amber-200">未开通</p>
            <p className="mt-2 text-sm text-zinc-400">
              OnlyBans 目前 demo 阶段，开通请添加：
            </p>
            <ul className="mt-2 text-sm text-zinc-300 space-y-1">
              <li>· 微信：<span className="font-mono">heha3737</span></li>
              <li>· Telegram：<span className="font-mono">@hunknownz</span></li>
            </ul>
            <p className="mt-3 text-xs text-zinc-500">
              备注你的注册邮箱（{me.user.email}），我们会手动开通。
            </p>
          </div>
        )}
      </section>

      <section>
        <button
          onClick={onLogout}
          className="text-sm text-zinc-400 underline hover:text-zinc-200"
        >
          退出登录
        </button>
      </section>
    </div>
  )
}
